import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface PublicMenuSettings {
  publicMenuEnabled: boolean;
  customerOrderingEnabled: boolean;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  deliveryFee: number;
  minOrderAmount: number;
  deliveryTimeEstimate: string;
  whatsappNumber: string | null;
  autoApproveTableOrders: boolean;
}

export function usePublicMenuSettings() {
  const { activeRestaurant, refetch } = useRestaurantContext();
  const [settings, setSettings] = useState<PublicMenuSettings>({
    publicMenuEnabled: false,
    customerOrderingEnabled: false,
    deliveryEnabled: false,
    pickupEnabled: false,
    deliveryFee: 0,
    minOrderAmount: 0,
    deliveryTimeEstimate: "30-45 min",
    whatsappNumber: null,
    autoApproveTableOrders: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (activeRestaurant) {
      // Type assertion to access the new fields
      const restaurant = activeRestaurant as typeof activeRestaurant & {
        public_menu_enabled?: boolean;
        customer_ordering_enabled?: boolean;
        delivery_enabled?: boolean;
        pickup_enabled?: boolean;
        delivery_fee?: number;
        min_order_amount?: number;
        delivery_time_estimate?: string;
        whatsapp_number?: string | null;
        auto_approve_table_orders?: boolean;
      };

      setSettings({
        publicMenuEnabled: restaurant.public_menu_enabled ?? false,
        customerOrderingEnabled: restaurant.customer_ordering_enabled ?? false,
        deliveryEnabled: restaurant.delivery_enabled ?? false,
        pickupEnabled: restaurant.pickup_enabled ?? false,
        deliveryFee: restaurant.delivery_fee ?? 0,
        minOrderAmount: restaurant.min_order_amount ?? 0,
        deliveryTimeEstimate: restaurant.delivery_time_estimate ?? "30-45 min",
        whatsappNumber: restaurant.whatsapp_number ?? null,
        autoApproveTableOrders: restaurant.auto_approve_table_orders ?? false,
      });
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const updateSettings = async (newSettings: Partial<PublicMenuSettings>): Promise<boolean> => {
    if (!activeRestaurant) return false;

    setIsUpdating(true);
    try {
      const updateData: Record<string, unknown> = {};
      
      if (newSettings.publicMenuEnabled !== undefined) {
        updateData.public_menu_enabled = newSettings.publicMenuEnabled;
      }
      if (newSettings.customerOrderingEnabled !== undefined) {
        updateData.customer_ordering_enabled = newSettings.customerOrderingEnabled;
      }
      if (newSettings.deliveryEnabled !== undefined) {
        updateData.delivery_enabled = newSettings.deliveryEnabled;
      }
      if (newSettings.pickupEnabled !== undefined) {
        updateData.pickup_enabled = newSettings.pickupEnabled;
      }
      if (newSettings.deliveryFee !== undefined) {
        updateData.delivery_fee = newSettings.deliveryFee;
      }
      if (newSettings.minOrderAmount !== undefined) {
        updateData.min_order_amount = newSettings.minOrderAmount;
      }
      if (newSettings.deliveryTimeEstimate !== undefined) {
        updateData.delivery_time_estimate = newSettings.deliveryTimeEstimate;
      }
      if (newSettings.whatsappNumber !== undefined) {
        updateData.whatsapp_number = newSettings.whatsappNumber;
      }
      if (newSettings.autoApproveTableOrders !== undefined) {
        updateData.auto_approve_table_orders = newSettings.autoApproveTableOrders;
      }

      const { error } = await supabase
        .from("restaurants")
        .update(updateData)
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      setSettings((prev) => ({ ...prev, ...newSettings }));
      await refetch();
      toast.success("Configurações salvas!");
      return true;
    } catch (error) {
      console.error("Erro ao atualizar configurações:", error);
      toast.error("Erro ao salvar configurações");
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    settings,
    isLoading,
    isUpdating,
    updateSettings,
  };
}
